import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

/**
 * Settings -> Slack.
 *
 * WHAT THIS PAGE IS FOR, BEYOND EDITING FIELDS
 *
 * Connecting a Slack app is a multi-step job across two products, and every step that goes
 * wrong presents identically: the bot never answers. So this page does three things a plain
 * settings form would not:
 *
 *  - it GENERATES the Slack-side manifest from our own settings, with our callback URLs
 *    already filled in, because a hand-typed request URL is a silent misconfiguration;
 *  - it names, beside each requested scope, the feature that needs it -- an admin asked to
 *    install an app that reads user emails is entitled to see why without reading code;
 *  - it shows the last inbound request and the last verification outcome, so a wrong signing
 *    secret is VISIBLE rather than silent.
 *
 * The integration is disabled by default and cannot be enabled until a live connectivity
 * check passes, and a failure names the check that failed.
 *
 * Secrets are write-only: the API returns has_bot_token / has_signing_secret booleans and
 * never the values, and submitting a blank field KEEPS what is stored.
 */

interface ScopeRow {
  scope: string
  feature: string
  without_it: string
}

interface SetupStep {
  title: string
  url: string
  needs_workspace_admin: boolean
  detail: string
}

interface AbsentScopeRow {
  scope: string
  why_absent: string
}

interface DriftItem {
  change: string
  now: string
  screen: string
  needs_reinstall: boolean
  detail: string
}

interface Drift {
  can_detect: boolean
  why: string
  items: DriftItem[]
  reinstall_note: string
}

interface Setup {
  slash_command: string
  app_display_name?: string
  bot_user_name?: string
  endpoints: Record<string, string>
  steps: SetupStep[]
  scopes: ScopeRow[]
  absent_scopes?: AbsentScopeRow[]
  bot_events: string[]
  app_home?: Record<string, boolean>
  drift?: Drift
}

interface InvocationTeam {
  team_id: string | null
  first_seen: string
  last_seen: string
  count: number
  routes: string[]
}

interface WorkspaceDiagnostics {
  installed_team_id: string | null
  installed_team_name: string | null
  invocation_teams: InvocationTeam[]
  agreement: 'not_checked' | 'no_invocations_seen' | 'mismatch' | 'agree'
  checking: boolean
  note: string
}

interface Diagnostics {
  worker_scoped: boolean
  last_inbound_request: {
    at: string
    route: string
    body_bytes: number
    has_signature: boolean
    retry_num?: string | null
    retry_reason?: string | null
  } | null
  last_verification: { at: string; ok: boolean; outcome: string; detail: string } | null
  recent_delivery_failures: { at: string; method: string; error: string }[]
  command_lane_depth: number
  workspace?: WorkspaceDiagnostics
}

interface SlackSettings {
  enabled: boolean
  has_bot_token: boolean
  has_signing_secret: boolean
  default_report_window: string
  max_batch_size: number
  max_live_messages: number
  live_update_interval_seconds: number
  interval_floor_seconds: number
  installed_team_id: string | null
  installed_team_name: string | null
  updated_at: string | null
  updated_by: string | null
  setup: Setup
  fqdn_configured: boolean
  fqdn_problem?: string
  diagnostics: Diagnostics
}

interface CheckResult {
  ok: boolean
  check: string
  detail: string
  team?: string
  team_id?: string
  bot_user_id?: string
  // M14 — the granted-scope verdict. `scopes_verified: false` means the check could NOT run
  // (Slack sent no X-OAuth-Scopes header), which is NOT evidence that a scope is missing, so
  // it is rendered as "unverified" rather than as a pass or a failure.
  scopes_verified?: boolean
  scopes_note?: string
  granted_scopes?: string[]
  missing_scopes?: string[]
}

const card: React.CSSProperties = {
  background: theme.bgAlt,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '1.25rem',
  marginBottom: '1.25rem',
}

const label: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  color: theme.textMuted,
  marginBottom: '0.25rem',
}

const input: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  padding: '0.45rem 0.6rem',
  background: theme.bg,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 6,
  fontFamily: 'inherit',
}

const button: React.CSSProperties = {
  padding: '0.45rem 0.9rem',
  background: theme.primary,
  color: theme.onPrimary,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
}

const buttonQuiet: React.CSSProperties = {
  ...button,
  background: 'transparent',
  color: theme.text,
  border: `1px solid ${theme.border}`,
  fontWeight: 500,
}

export default function SlackSettings() {
  const isAdmin = useIsAdmin()
  const [settings, setSettings] = useState<SlackSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [check, setCheck] = useState<CheckResult | null>(null)
  const [manifest, setManifest] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Blank means KEEP. These start empty on every load precisely so that saving without
  // touching them cannot clear a stored secret.
  const [botToken, setBotToken] = useState('')
  const [signingSecret, setSigningSecret] = useState('')
  const [maxBatchSize, setMaxBatchSize] = useState<number | ''>('')
  const [maxLive, setMaxLive] = useState<number | ''>('')
  const [interval, setIntervalSeconds] = useState<number | ''>('')
  const [reportWindow, setReportWindow] = useState('')

  const load = () => {
    setLoading(true)
    fetch('/api/settings/slack')
      .then(async r => {
        if (!r.ok) throw new Error((await r.json()).detail || 'Could not load Slack settings')
        return r.json()
      })
      .then((data: SlackSettings) => {
        setSettings(data)
        setError(null)
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const save = async (enabled?: boolean) => {
    setSaving(true)
    setError(null)
    setNotice(null)
    const body: Record<string, unknown> = {}
    // Whether a NEW token is being sent, captured before the fields are cleared below. This
    // is the H8 case: the plain Save button omits `enabled`, so the backend does not re-run
    // the connectivity check, and a changed token clears the recorded workspace on purpose —
    // the operator has to be told, or the identity comparison stays off silently.
    const sendingBotToken = botToken.trim().length > 0
    if (sendingBotToken) body.bot_token = botToken.trim()
    if (signingSecret.trim()) body.signing_secret = signingSecret.trim()
    if (maxBatchSize !== '') body.max_batch_size = maxBatchSize
    if (maxLive !== '') body.max_live_messages = maxLive
    if (interval !== '') body.live_update_interval_seconds = interval
    if (reportWindow) body.default_report_window = reportWindow
    if (enabled !== undefined) body.enabled = enabled

    const res = await fetch('/api/settings/slack', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    setSaving(false)
    if (res.ok) {
      const saved: SlackSettings = await res.json().catch(() => ({}) as SlackSettings)
      setBotToken('')
      setSigningSecret('')
      const base =
        enabled === true ? 'Enabled.' : enabled === false ? 'Disabled.' : 'Saved.'
      // Read off the RESPONSE, not off `sendingBotToken` alone: the backend clears the
      // workspace only when the token actually changed, so re-pasting the same token keeps it
      // and must not produce a warning telling the operator to fix something that is fine.
      //
      // Saving a CHANGED token now re-derives the workspace automatically on the server, so
      // this response distinguishes the two outcomes with no extra field: a populated
      // installed_team_id means the automatic check succeeded, and a null one means it did not
      // (the token is stored either way). The old copy told the operator to "run the
      // connectivity check below" for a check that has already been attempted.
      const workspaceRecorded = sendingBotToken && !!saved.installed_team_id
      const workspaceCleared = sendingBotToken && !saved.installed_team_id
      setNotice(
        workspaceRecorded
          ? `${base} The bot token changed, and the connectivity check re-derived the Slack ` +
              `workspace: ${saved.installed_team_name || 'unnamed'} ` +
              `(${saved.installed_team_id}). The identity comparison is on.`
          : workspaceCleared
            ? `${base} The bot token changed, so the recorded Slack workspace was cleared, ` +
              'and the connectivity check run automatically afterwards did NOT succeed — so ' +
              'the workspace is still unrecorded and the identity comparison is OFF. Run the ' +
              'check below to see the reason; until it passes, privileged commands fall back ' +
              'to resolving users without a workspace check.'
            : base,
      )
      load()
    } else {
      const payload = await res.json().catch(() => ({}))
      setError(payload.detail || 'Save failed')
    }
  }

  const runCheck = async () => {
    setCheck(null)
    const res = await fetch('/api/settings/slack/test', { method: 'POST' })
    setCheck(await res.json())
  }

  const loadManifest = async () => {
    const res = await fetch('/api/settings/slack/manifest')
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}))
      setError(payload.detail || 'Could not generate the manifest')
      return
    }
    setManifest((await res.json()).manifest)
  }

  const disconnect = async () => {
    if (
      !confirm(
        'Delete the stored bot token and signing secret and stop responding to Slack?\n\n' +
          'This is LOCAL: the app stays installed in Slack, which only a Slack workspace ' +
          'admin can undo there.',
      )
    )
      return
    const res = await fetch('/api/settings/slack/disconnect', { method: 'POST' })
    const payload = await res.json().catch(() => ({}))
    setNotice(payload.note || 'Disconnected.')
    load()
  }

  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Slack</h2>
        <AdminOnly what="Slack integration settings" />
      </div>
    )
  }
  if (loading) return <div>Loading…</div>
  if (!settings) return <div style={{ color: theme.primary }}>{error || 'Unavailable'}</div>

  const canEnable = settings.has_bot_token && settings.has_signing_secret
  const s = settings

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>Slack</h2>
      <p style={{ color: theme.textMuted, fontSize: '0.88rem', lineHeight: 1.6 }}>
        A two-way bot: anyone in the workspace can ask for the latest run and SLA reports, and
        a Slack user whose email matches an e2e_scheduler <strong>admin</strong> can trigger a
        test run or a tactical batch.
      </p>

      {error && (
        <div style={{ ...card, borderColor: theme.primary, color: theme.primary }}>{error}</div>
      )}
      {notice && <div style={{ ...card, color: theme.text }}>{notice}</div>}

      {/* --- state ------------------------------------------------------------------ */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Connection</h3>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div>
            <div style={label}>Status</div>
            <strong style={{ color: s.enabled ? '#4caf50' : theme.textMuted }}>
              {s.enabled ? 'Enabled' : 'Disabled'}
            </strong>
          </div>
          <div>
            <div style={label}>Bot token</div>
            <strong>{s.has_bot_token ? 'stored' : 'not set'}</strong>
          </div>
          <div>
            <div style={label}>Signing secret</div>
            <strong>{s.has_signing_secret ? 'stored' : 'not set'}</strong>
          </div>
          {/*
            The recorded workspace, ON THE CONNECTION CARD and not only in the diagnostics
            section further down. It is the value the identity comparison is made against, and
            NULL means the comparison is OFF — so an operator reading "Enabled / stored /
            stored" and a green connectivity check had every visible signal saying healthy
            while privileged commands could not resolve anyone. Rendered here because this is
            the panel that is actually read after saving a token.
          */}
          <div>
            <div style={label}>Recorded workspace</div>
            {s.installed_team_id ? (
              <strong>
                {s.installed_team_name || 'unnamed'} (<code>{s.installed_team_id}</code>)
              </strong>
            ) : (
              <strong style={{ color: theme.primary }}>
                not recorded — identity comparison OFF
              </strong>
            )}
          </div>
          <div>
            <div style={label}>Last changed</div>
            <strong>{s.updated_at ? `${s.updated_at} by ${s.updated_by || '—'}` : '—'}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button style={buttonQuiet} onClick={runCheck}>
            Run connectivity check
          </button>
          {!s.enabled && (
            <button style={button} disabled={!canEnable || saving} onClick={() => save(true)}>
              Enable
            </button>
          )}
          {s.enabled && (
            <button style={buttonQuiet} disabled={saving} onClick={() => save(false)}>
              Disable
            </button>
          )}
          <button style={buttonQuiet} onClick={disconnect}>
            Disconnect workspace
          </button>
        </div>
        {!canEnable && (
          <p style={{ color: theme.textMuted, fontSize: '0.82rem', marginBottom: 0 }}>
            Both secrets must be stored before the integration can be enabled, and enabling
            also requires the live <code>auth.test</code> check to pass.
          </p>
        )}
        {check && (
          <p
            style={{
              marginBottom: 0,
              fontSize: '0.85rem',
              color: check.ok ? '#4caf50' : theme.primary,
            }}
          >
            {check.ok ? '✓' : '✗'} {check.check}: {check.detail}
            {check.team ? ` (workspace: ${check.team})` : ''}
          </p>
        )}
        {check && check.ok && check.scopes_verified === true && (
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
            ✓ Granted scopes verified against the {check.granted_scopes?.length ?? 0} this app
            requests — none missing.
          </p>
        )}
        {check && check.scopes_verified === false && (
          /* NOT a failure and NOT a pass. Saying "verified" here would assert a check nobody
             ran, which is the same trap as a stale gate-warning: it teaches the operator to
             distrust the reading. */
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
            ⚠ Granted scopes could not be verified. {check.scopes_note}
          </p>
        )}
      </div>

      {/* --- credentials ------------------------------------------------------------ */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Credentials</h3>
        <p style={{ color: theme.textMuted, fontSize: '0.82rem', marginTop: 0 }}>
          Stored AES-256-GCM encrypted and never returned by the API. Leave a field{' '}
          <strong>blank to keep</strong> what is already stored.
        </p>
        <div style={{ marginBottom: '0.9rem' }}>
          <label style={label} htmlFor="slack-bot-token">
            Bot User OAuth Token (xoxb-…)
          </label>
          <input
            id="slack-bot-token"
            style={input}
            type="password"
            autoComplete="off"
            value={botToken}
            placeholder={s.has_bot_token ? '•••••••• (stored)' : 'xoxb-…'}
            onChange={e => setBotToken(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: '0.9rem' }}>
          <label style={label} htmlFor="slack-signing-secret">
            Signing Secret
          </label>
          <input
            id="slack-signing-secret"
            style={input}
            type="password"
            autoComplete="off"
            value={signingSecret}
            placeholder={s.has_signing_secret ? '•••••••• (stored)' : 'from Basic Information'}
            onChange={e => setSigningSecret(e.target.value)}
          />
          <p style={{ color: theme.textMuted, fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
            Verification is fail-closed: without this, every inbound Slack request is refused.
            There is no permissive mode.
          </p>
        </div>
      </div>

      {/* --- limits ----------------------------------------------------------------- */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Limits</h3>
        <div style={{ display: 'grid', gap: '0.9rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={label} htmlFor="slack-max-batch">
              Maximum batch size from Slack (now {s.max_batch_size})
            </label>
            <input
              id="slack-max-batch"
              style={input}
              type="number"
              min={1}
              max={50}
              value={maxBatchSize}
              onChange={e => setMaxBatchSize(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <p style={{ color: theme.textMuted, fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
              A launch above this is refused. Enforced <em>alongside</em> the mandatory
              confirmation, not instead of it.
            </p>
          </div>
          <div>
            <label style={label} htmlFor="slack-max-live">
              Concurrent live-updating messages (now {s.max_live_messages})
            </label>
            <input
              id="slack-max-live"
              style={input}
              type="number"
              min={1}
              max={50}
              value={maxLive}
              onChange={e => setMaxLive(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <p style={{ color: theme.textMuted, fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
              Beyond this the refresh interval <em>degrades</em> and the message says so — the
              feature slows rather than failing.
            </p>
          </div>
          <div>
            <label style={label} htmlFor="slack-interval">
              Refresh interval, seconds (now {s.live_update_interval_seconds}, floor{' '}
              {s.interval_floor_seconds})
            </label>
            <input
              id="slack-interval"
              style={input}
              type="number"
              min={s.interval_floor_seconds}
              max={600}
              value={interval}
              onChange={e => setIntervalSeconds(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
          <div>
            <label style={label} htmlFor="slack-window">
              Default report window (now {s.default_report_window})
            </label>
            <select
              id="slack-window"
              style={input}
              value={reportWindow}
              onChange={e => setReportWindow(e.target.value)}
            >
              <option value="">keep</option>
              <option value="24h">24h</option>
              <option value="7d">7d</option>
              <option value="30d">30d</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <button style={button} disabled={saving} onClick={() => save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* --- setup ------------------------------------------------------------------ */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Slack-side setup</h3>
        {!s.fqdn_configured && (
          <p style={{ color: theme.primary, fontSize: '0.85rem' }}>
            {s.fqdn_problem || 'PUBLIC_FQDN is not configured, so callback URLs cannot be built.'}
          </p>
        )}
        <ol style={{ paddingLeft: '1.2rem', fontSize: '0.86rem', lineHeight: 1.7 }}>
          {s.setup.steps.map(step => (
            <li key={step.title} style={{ marginBottom: '0.5rem' }}>
              <a href={step.url} target="_blank" rel="noreferrer" style={{ color: theme.primary }}>
                {step.title}
              </a>
              {step.needs_workspace_admin && (
                <span
                  style={{
                    marginLeft: '0.5rem',
                    fontSize: '0.72rem',
                    padding: '0.1rem 0.4rem',
                    borderRadius: 4,
                    border: `1px solid ${theme.primary}`,
                    color: theme.primary,
                  }}
                >
                  Slack workspace admin only
                </span>
              )}
              <div style={{ color: theme.textMuted }}>{step.detail}</div>
            </li>
          ))}
        </ol>

        <div style={{ fontSize: '0.84rem', marginBottom: '0.75rem' }}>
          <div style={label}>Our callback URLs (already in the manifest)</div>
          {Object.entries(s.setup.endpoints).map(([name, url]) => (
            <div key={name}>
              <code>{name}</code>: <code>{url}</code>
            </div>
          ))}
          {Object.keys(s.setup.endpoints).length === 0 && <em>unavailable until PUBLIC_FQDN is set</em>}
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button style={buttonQuiet} onClick={loadManifest}>
            Show app manifest
          </button>
          <a
            style={{ ...buttonQuiet, textDecoration: 'none', display: 'inline-block' }}
            href="/api/settings/slack/manifest?download=true"
          >
            Download manifest
          </a>
          {manifest && (
            <button
              style={buttonQuiet}
              onClick={() => navigator.clipboard?.writeText(manifest)}
            >
              Copy
            </button>
          )}
        </div>
        {manifest && (
          <pre
            style={{
              marginTop: '0.9rem',
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              padding: '0.75rem',
              maxHeight: 320,
              overflow: 'auto',
              fontSize: '0.76rem',
            }}
          >
            {manifest}
          </pre>
        )}
      </div>

      {/* --- scopes ----------------------------------------------------------------- */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.5rem' }}>Requested scopes, and why</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
          <thead>
            <tr style={{ background: theme.bgElevated }}>
              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Scope</th>
              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Feature that needs it</th>
              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Without it</th>
            </tr>
          </thead>
          <tbody>
            {s.setup.scopes.map(row => (
              <tr key={row.scope} style={{ borderTop: `1px solid ${theme.border}` }}>
                <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>
                  <code>{row.scope}</code>
                </td>
                <td style={{ padding: '0.4rem' }}>{row.feature}</td>
                <td style={{ padding: '0.4rem', color: theme.textMuted }}>{row.without_it}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ color: theme.textMuted, fontSize: '0.8rem', marginBottom: 0 }}>
          Subscribed events: {s.setup.bot_events.map(e => <code key={e}>{e} </code>)}
        </p>
        {/*
          WAVE A ITEM 16 — the "Deliberately NOT requested" block (`im:write`, `im:read`) was
          removed from this SURFACE, not from the design. The scopes are still absent, still
          absent for the same reason, and the server still serves the reasons: they are
          `ABSENT_SCOPES` in `backend/services/slack/manifest.py`, pinned by
          `tests/test_slack_manifest.py`, and reachable to a reader in
          `frontend/src/help/content.ts` under "Why the bot cannot post to a channel". G6.21a
          — the bot never speaks first — is enforced by there being no handler and no scope,
          never by prose on this page.
        */}
      </div>

      {/*
        WAVE A ITEM 17 — the "Is your installed app older than this manifest?" box was removed
        from this surface, container and all. The server still computes it: `GET
        /api/settings/slack` carries `setup.drift`, so nothing was un-built and a reader can
        still see which Slack screens to edit and which change needs a reinstall. The
        Diagnostics section below is deliberately UNTOUCHED — it is the live-request evidence,
        not manifest advice, and the two were only ever adjacent.
      */}
      {/* --- diagnostics ------------------------------------------------------------ */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.5rem' }}>Diagnostics</h3>
        <p style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 0 }}>
          This is <strong>one worker's view</strong>. The app runs two gunicorn workers and a
          request is served by whichever the load balancer picks, so a request handled by the
          other worker will not appear here. A wrong signing secret still shows up, because both
          workers share the same stored secret.
        </p>
        <div style={{ fontSize: '0.84rem', lineHeight: 1.7 }}>
          <div>
            <strong>Last inbound request:</strong>{' '}
            {s.diagnostics.last_inbound_request ? (
              <>
                {s.diagnostics.last_inbound_request.at} — {s.diagnostics.last_inbound_request.route}
                , {s.diagnostics.last_inbound_request.body_bytes} bytes, signature header{' '}
                {s.diagnostics.last_inbound_request.has_signature ? 'present' : 'ABSENT'}
                {s.diagnostics.last_inbound_request.retry_num
                  ? `, Slack retry #${s.diagnostics.last_inbound_request.retry_num} (${
                      s.diagnostics.last_inbound_request.retry_reason || 'no reason given'
                    })`
                  : ''}
              </>
            ) : (
              <em>none seen by this worker</em>
            )}
          </div>
          <div>
            <strong>Last verification:</strong>{' '}
            {s.diagnostics.last_verification ? (
              <span
                style={{ color: s.diagnostics.last_verification.ok ? '#4caf50' : theme.primary }}
              >
                {s.diagnostics.last_verification.at} — {s.diagnostics.last_verification.outcome}:{' '}
                {s.diagnostics.last_verification.detail}
              </span>
            ) : (
              <em>none seen by this worker</em>
            )}
          </div>
          <div>
            <strong>Command lane depth:</strong> {s.diagnostics.command_lane_depth}
          </div>

          {/* Workspace. The comparison whose absence presented as `user_not_found`: a
              signing secret is per APP, so a request from a workspace our bot token does not
              belong to verifies cleanly and then cannot resolve any user. */}
          {s.diagnostics.workspace && (
            <div
              style={{
                marginTop: '0.75rem',
                paddingTop: '0.75rem',
                borderTop: `1px solid ${theme.border}`,
              }}
            >
              <strong>Workspace</strong>
              <div>
                Installed in:{' '}
                {s.diagnostics.workspace.installed_team_id ? (
                  <>
                    {s.diagnostics.workspace.installed_team_name || 'unnamed'} (
                    <code>{s.diagnostics.workspace.installed_team_id}</code>)
                  </>
                ) : (
                  <em>not recorded — run the connectivity check</em>
                )}
              </div>
              <div>
                Seen on invocations:{' '}
                {s.diagnostics.workspace.invocation_teams.length === 0 ? (
                  <em>none seen by this worker</em>
                ) : (
                  s.diagnostics.workspace.invocation_teams.map(t => (
                    <span key={t.team_id || 'none'} style={{ marginRight: '0.6rem' }}>
                      <code>{t.team_id || 'none reported'}</code> ×{t.count}
                    </span>
                  ))
                )}
              </div>
              <div
                style={{
                  color:
                    s.diagnostics.workspace.agreement === 'mismatch'
                      ? theme.primary
                      : s.diagnostics.workspace.agreement === 'agree'
                        ? '#4caf50'
                        : theme.textMuted,
                }}
              >
                {s.diagnostics.workspace.agreement === 'agree' && '✓ they agree'}
                {s.diagnostics.workspace.agreement === 'mismatch' && '✗ they DISAGREE'}
                {s.diagnostics.workspace.agreement === 'not_checked' &&
                  '⚠ not being checked'}
                {s.diagnostics.workspace.agreement === 'no_invocations_seen' &&
                  '— nothing to compare yet'}
                {' — '}
                {s.diagnostics.workspace.note}
              </div>
            </div>
          )}
          <div style={{ marginTop: '0.5rem' }}>
            <strong>Recent delivery failures:</strong>
            {s.diagnostics.recent_delivery_failures.length === 0 ? (
              <em> none</em>
            ) : (
              <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                {s.diagnostics.recent_delivery_failures.map((f, i) => (
                  <li key={`${f.at}-${i}`}>
                    {f.at} — <code>{f.method}</code>: {f.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div style={{ marginTop: '0.9rem' }}>
          <button style={buttonQuiet} onClick={load}>
            Refresh
          </button>
        </div>
      </div>
    </div>
  )
}
